import { useState, useEffect, useRef } from "react";
import { ImageOverlay, useMapEvents } from "react-leaflet";
import "./styles.css";
import { Z } from "../../global/constants/zIndex";

interface TemporalWMSLayerProps {
  typename: string;
  workspace: string;
  zIndex?: number;
  timeValue?: string;
  opacity?: number;
}

const GEOSERVER_URL = "https://geoserver-atlas.isitics.com/geoserver";

interface ImageState {
  url: string;
  bounds: [[number, number], [number, number]];
  key: string;
}

/**
 * Optimized WMS raster layer with true crossfade and minimal re-renders.
 */
export function TemporalWMSLayer({
  typename,
  workspace,
  zIndex = Z.MAP_LAYER_DEFAULT,
  timeValue,
  opacity = 0.8,
}: TemporalWMSLayerProps) {
  const [oldImage, setOldImage] = useState<ImageState | null>(null);
  const [currentImage, setCurrentImage] = useState<ImageState | null>(null);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const requestIdRef = useRef(0);
  const preloadImageRef = useRef<HTMLImageElement | null>(null);
  const cleanupTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Use refs to avoid circular dependencies
  const currentImageRef = useRef<ImageState | null>(null);
  currentImageRef.current = currentImage;

  const map = useMapEvents({
    moveend: () => {
      scheduleUpdate();
    },
    zoomend: () => {
      scheduleUpdate();
    },
  });

  // Build WMS URL - no dependencies that change
  const buildImageUrl = () => {
    if (!map) return null;

    const mapBounds = map.getBounds();
    const size = map.getSize();
    if (!size) return null;

    const bbox = `${mapBounds.getWest()},${mapBounds.getSouth()},${mapBounds.getEast()},${mapBounds.getNorth()}`;

    return (
      `${GEOSERVER_URL}/${workspace}/wms?` +
      `service=WMS&version=1.1.0&request=GetMap&` +
      `layers=${workspace}:${typename}&` +
      `bbox=${bbox}&width=${size.x}&height=${size.y}&` +
      `srs=EPSG:4326&format=image/png&transparent=true` +
      (timeValue ? `&time=${timeValue}` : ``)
    );
  };

  // Preload and swap images
  const loadImage = (url: string, bounds: [[number, number], [number, number]], requestId: number) => {
    // Cancel existing preload
    if (preloadImageRef.current) {
      preloadImageRef.current.onload = null;
      preloadImageRef.current.onerror = null;
      preloadImageRef.current.src = "";
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    preloadImageRef.current = img;

    img.onload = () => {
      if (requestId === requestIdRef.current) {
        const newImageState: ImageState = {
          url,
          bounds,
          key: `${Date.now()}`,
        };

        // Crossfade: current becomes old, new becomes current
        setOldImage(currentImageRef.current);
        setCurrentImage(newImageState);

        // Clean up old image after transition
        if (cleanupTimerRef.current) {
          clearTimeout(cleanupTimerRef.current);
        }
        cleanupTimerRef.current = setTimeout(() => {
          setOldImage(null);
        }, 250);
      }
    };

    img.onerror = () => {
      console.error("Failed to load WMS image");
    };

    img.src = url;
  };

  // Schedule update with debounce
  const scheduleUpdate = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const currentRequestId = ++requestIdRef.current;

    debounceTimerRef.current = setTimeout(() => {
      if (currentRequestId === requestIdRef.current && map) {
        const url = buildImageUrl();
        if (!url) return;

        const mapBounds = map.getBounds();
        const bounds: [[number, number], [number, number]] = [
          [mapBounds.getSouth(), mapBounds.getWest()],
          [mapBounds.getNorth(), mapBounds.getEast()],
        ];

        loadImage(url, bounds, currentRequestId);
      }
    }, 50);
  };

  // Only trigger on timeValue changes - NO FUNCTION DEPENDENCIES
  useEffect(() => {
    scheduleUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeValue]);

  // Initial load - runs ONCE
  useEffect(() => {
    scheduleUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
     return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (cleanupTimerRef.current) clearTimeout(cleanupTimerRef.current);
      if (preloadImageRef.current) {
        preloadImageRef.current.onload = null;
        preloadImageRef.current.onerror = null;
        preloadImageRef.current.src = "";
      }
    };
  }, []);

  return (
    <>
      {oldImage && (
        <ImageOverlay
          key={oldImage.key}
          url={oldImage.url}
          bounds={oldImage.bounds}
          opacity={0}
          zIndex={zIndex}
          pane="tilePane"
          className="seamless-overlay old-overlay"
          crossOrigin="anonymous"
        />
      )}

      {currentImage && (
        <ImageOverlay
          key={currentImage.key}
          url={currentImage.url}
          bounds={currentImage.bounds}
          opacity={opacity}
          zIndex={zIndex + 1}
          pane="tilePane"
          className="seamless-overlay current-overlay"
          crossOrigin="anonymous"
        />
      )}
    </>
  );
}
